import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { fit_level } from './table_layout';

const observe = (el, on_width) => {
  if (!el) return () => {};
  on_width(el.getBoundingClientRect().width);
  if (typeof ResizeObserver === 'undefined') return () => {};
  const ro = new ResizeObserver((entries) => {
    const entry = entries[entries.length - 1];
    if (entry) on_width(entry.contentRect.width);
  });
  ro.observe(el);
  return () => ro.disconnect();
};

const next_state = (s, width, needs, opts) => {
  const mode = fit_level(width, s.mode, { ...opts, needs });
  return width === s.width && mode === s.mode && needs === s.needs ? s : { width, mode, needs };
};

/*
 * the container's width and the layout for it (table_layout.fit_level): cards below the threshold,
 * else a table with the fewest folded columns that fits. The rendered table reports the width it
 * needed when it overflowed (on_overflow), which folds one more column; new content (`content`)
 * forgets those measurements so fewer folds are tried again.
 */
export const useFitLayout = ({ threshold, levels = 0, content } = {}) => {
  const ref = useRef(null);
  const opts = { threshold, levels };
  const [state, setState] = useState({ width: 0, mode: 0, needs: {} });
  useLayoutEffect(
    () =>
      observe(ref.current, (w) =>
        setState((s) => next_state(s, Math.round(w), s.needs, { threshold, levels }))
      ),
    [threshold, levels]
  );
  useLayoutEffect(() => {
    setState((s) => (Object.keys(s.needs).length ? next_state(s, s.width, {}, opts) : s));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);
  const on_overflow = useCallback(
    (layout, need) =>
      setState((s) =>
        s.mode !== layout || (s.needs[layout] || 0) >= need
          ? s
          : next_state(s, s.width, { ...s.needs, [layout]: need }, { threshold, levels })
      ),
    [threshold, levels]
  );
  return { ref, mode: state.mode, width: state.width, on_overflow };
};

/*
 * reports when the element's content is wider than the element (the table would need sideways
 * scroll): on_overflow(width it needs). Re-checks on resize of the element or its first child and
 * after every render.
 */
export const useOverflowReport = (width, on_overflow) => {
  const ref = useRef(null);
  const check = useCallback(() => {
    const el = ref.current;
    if (!el || !(width > 0)) return;
    const extra = el.scrollWidth - el.clientWidth;
    if (extra > 1) on_overflow(width + extra);
  }, [width, on_overflow]);
  useLayoutEffect(check);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => check());
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [check]);
  return ref;
};
